// =============================================================================
// newborn.tsx — Beshik to'y (Uzbek cradle ceremony) guest-page variant.
//
// Cultural intent:
//   The beshik to'y welcomes a newborn into their family with their first
//   carved wooden cradle. It is gentler and more familial than a Western
//   "baby shower": grandmothers bless the child, an "alla" (lullaby) is sung,
//   and the room is hushed. Every design choice here leans into that —
//   nursery pastels, Lora warmth, slow fades, and a single signature motion:
//   a tiny SVG beshik that rocks three times on hero load before settling,
//   ringing a soft chime when it comes to rest.
//
// Signature element (unique to Beshik):
//   * `BeshikRocker` — an SVG carved cradle that rocks gently from −3° → +3°
//     three times, then settles back to 0°. Plays a single `chime` on
//     completion (gated by useSound). Reduced-motion fallback: static, no
//     chime.
//
// Other distinctive details:
//   * Hero subtitle is a traditional Uzbek "alla" lullaby line in italic
//     Lora — public-domain folk verse.
//   * Three-photo triptych tilted at −2°, 0°, +2° with a soft oat gradient
//     overlay, like prints in a family album. Hover straightens and warms.
//   * Baby-foot SVGs serve as section dividers between the hero, story, and
//     triptych.
//   * Section reveal: each major <section> fades in over 1.5s when scrolled
//     into view (IntersectionObserver), playing a low-volume `swoosh`.
//   * Photos receive the same soft "family-album" filter as the card.
//
// Sound triggers (3, all gated by useSound + reduced-motion):
//   1. RSVP submit ("yes") → `chime`
//   2. Any button tap (RSVP yes/no, RSVP submit count buttons) → `tap`
//   3. Major section first-reveal → `swoosh`
//   Plus: the rocker plays a single bonus `chime` on settle (one-shot).
//
// Accessibility:
//   * `prefers-reduced-motion: reduce` disables rocker animation, hero scale,
//     section fades, and triptych hover transforms. Sound is independently
//     gated through useSound's storage default.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from "react";
import {
  GuestPhoto,
  StorySection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// Soft "family-album" warm filter applied across every photo on the page.
const PHOTO_FILTER = "sepia(0.1) brightness(1.05) saturate(0.95)";

// Triptych tilts (deg) — left, centre, right. Module-level so identity is stable.
const TRIPTYCH_ROTATIONS = ["-2deg", "0deg", "2deg"] as const;

// ─── decorative SVGs ──────────────────────────────────────────────────────

// Tiny baby-foot pair, used as section dividers. Mobile shows a third print.
function FootprintDivider({ color }: { color: string }) {
  return (
    <svg
      width="80"
      height="22"
      viewBox="0 0 110 26"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <g fill={color}>
        <g opacity="0.8">
          <ellipse cx="14" cy="16" rx="4.4" ry="6" />
          <circle cx="9.5" cy="6.5" r="1.1" />
          <circle cx="13" cy="4.5" r="1.3" />
          <circle cx="16.5" cy="4.6" r="1.1" />
          <circle cx="19.5" cy="6.5" r="0.9" />
        </g>
        <g opacity="0.55" transform="translate(34 -2)">
          <ellipse cx="14" cy="16" rx="4" ry="5.4" />
          <circle cx="9.5" cy="7" r="1" />
          <circle cx="13" cy="5" r="1.2" />
          <circle cx="16.5" cy="5.2" r="1" />
          <circle cx="19.5" cy="7" r="0.8" />
        </g>
        {/* third print — only revealed via .bs-footprint-third on mobile */}
        <g opacity="0.4" transform="translate(68 1)" className="bs-footprint-third" style={{ display: "none" }}>
          <ellipse cx="14" cy="16" rx="3.6" ry="5" />
          <circle cx="9.5" cy="7.4" r="0.9" />
          <circle cx="13" cy="5.5" r="1.1" />
          <circle cx="16.5" cy="5.6" r="0.9" />
          <circle cx="19.5" cy="7.4" r="0.7" />
        </g>
      </g>
    </svg>
  );
}

// Carved beshik silhouette — used both behind the title at low opacity and
// inside `BeshikRocker` as the rocking object. Outline only.
function BeshikSilhouette({
  color,
  size = 120,
  showRockerLine = true,
}: {
  color: string;
  size?: number;
  showRockerLine?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size * 0.7}
      viewBox="0 0 140 100"
      fill="none"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <g
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* canopy / hood arch */}
        <path d="M28 38 Q 70 -2 112 38" />
        {/* finial atop hood */}
        <circle cx="70" cy="11.5" r="1.7" fill={color} stroke="none" />
        {/* tiny suzani-style starburst inside the hood */}
        <g opacity="0.55">
          <line x1="70" y1="22" x2="70" y2="32" />
          <line x1="62" y1="27" x2="78" y2="27" />
          <line x1="64" y1="22" x2="76" y2="32" />
          <line x1="76" y1="22" x2="64" y2="32" />
        </g>
        {/* bed frame */}
        <rect x="22" y="38" width="96" height="22" rx="2.5" />
        {/* spindles */}
        <line x1="42" y1="38" x2="42" y2="60" opacity="0.55" />
        <line x1="58" y1="38" x2="58" y2="60" opacity="0.55" />
        <line x1="74" y1="38" x2="74" y2="60" opacity="0.55" />
        <line x1="90" y1="38" x2="90" y2="60" opacity="0.55" />
        <line x1="106" y1="38" x2="106" y2="60" opacity="0.55" />
        {/* legs / posts */}
        <line x1="28" y1="60" x2="28" y2="68" />
        <line x1="48" y1="60" x2="48" y2="68" />
        <line x1="92" y1="60" x2="92" y2="68" />
        <line x1="112" y1="60" x2="112" y2="68" />
        {/* rocker arches (two semi-circles) */}
        <path d="M16 68 Q 38 92 60 68" />
        <path d="M80 68 Q 102 92 124 68" />
      </g>
      {showRockerLine && (
        <line
          x1="6"
          y1="92"
          x2="134"
          y2="92"
          stroke={color}
          strokeWidth="0.6"
          strokeOpacity="0.25"
        />
      )}
    </svg>
  );
}

// Signature element: a beshik that rocks 3 times on mount, then settles.
// Triggers a single `chime` on settle. Static under reduced-motion.
function BeshikRocker({
  color,
  reduceMotion,
  onSettle,
}: {
  color: string;
  reduceMotion: boolean;
  onSettle: () => void;
}) {
  const settled = useRef(false);

  useEffect(() => {
    if (reduceMotion) return;
    // Animation duration: 2.5s. Fire chime on settle exactly once.
    const id = window.setTimeout(() => {
      if (settled.current) return;
      settled.current = true;
      onSettle();
    }, 2500);
    return () => window.clearTimeout(id);
  }, [reduceMotion, onSettle]);

  return (
    <div
      style={{
        position: "relative",
        width: 160,
        height: 120,
        margin: "0 auto",
      }}
      aria-hidden="true"
    >
      <style>{`
        @keyframes bs-rock {
          0%   { transform: rotate(0deg); }
          12%  { transform: rotate(-3deg); }
          28%  { transform: rotate(3deg); }
          44%  { transform: rotate(-2.4deg); }
          60%  { transform: rotate(2.4deg); }
          76%  { transform: rotate(-1.6deg); }
          90%  { transform: rotate(1deg); }
          100% { transform: rotate(0deg); }
        }
        .bs-rocker-anim {
          transform-origin: 50% 92%;
          animation: bs-rock 2.5s ease-in-out 1 both;
        }
        @media (prefers-reduced-motion: reduce) {
          .bs-rocker-anim { animation: none !important; }
        }
      `}</style>
      <div className={reduceMotion ? "" : "bs-rocker-anim"} style={{ width: "100%", height: "100%" }}>
        <BeshikSilhouette color={color} size={160} />
      </div>
    </div>
  );
}

// ─── reveal-on-scroll wrapper ─────────────────────────────────────────────

interface RevealProps {
  children: React.ReactNode;
  reduceMotion: boolean;
  onReveal?: () => void;
  delay?: number;
}

// Wraps a section so it fades in (1.5s ease) when scrolled into view, and
// fires `onReveal` once. Under reduced-motion, renders children immediately
// without animation and without firing onReveal (no swoosh either).
function Reveal({ children, reduceMotion, onReveal, delay = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(reduceMotion);
  const firedRef = useRef(false);

  useEffect(() => {
    if (reduceMotion) {
      setRevealed(true);
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            setRevealed(true);
            if (onReveal) onReveal();
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reduceMotion, onReveal]);

  return (
    <div
      ref={ref}
      style={{
        opacity: revealed ? 1 : 0,
        transform: revealed ? "translateY(0)" : "translateY(14px)",
        transition: reduceMotion
          ? "none"
          : `opacity 1.5s ease ${delay}ms, transform 1.5s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 7. NEWBORN — beshik: gentle, simpler, baby-focused
// ═══════════════════════════════════════════════════════════════════════
export function VariantNewborn({ t, isDark, ctx }: VariantProps) {
  const p = t.photos || [];
  const { play } = useSound();
  const tt = useT();

  // ─ reduced-motion (kept reactive in case the user toggles in OS) ────────
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // ─ chime once when the rocker settles ────────────────────────────────────
  const rockerChimedRef = useRef(false);
  const handleRockerSettle = useMemo(
    () => () => {
      if (rockerChimedRef.current) return;
      rockerChimedRef.current = true;
      play("chime");
    },
    [play],
  );

  // ─ chime once when RSVP transitions to "yes" ────────────────────────────
  const rsvpChimedRef = useRef(false);
  useEffect(() => {
    if (ctx.rsvp.rsvp === "yes" && !rsvpChimedRef.current) {
      rsvpChimedRef.current = true;
      play("chime");
    }
  }, [ctx.rsvp.rsvp, play]);

  // Wrap RSVP setters so any user button press chirps a soft tap.
  const rsvpProxy = useMemo(
    () => ({
      ...ctx.rsvp,
      setRsvp: (v: string | null) => {
        play("tap");
        ctx.rsvp.setRsvp(v);
      },
      setGuests: (n: number) => {
        play("tap");
        ctx.rsvp.setGuests(n);
      },
    }),
    [ctx.rsvp, play],
  );

  return (
    <div className="beshik-variant">
      {/* Page-scoped CSS — keyframes and reduced-motion guards. */}
      <style>{`
        @keyframes bs-hero-fade {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes bs-photo-in {
          0%   { opacity: 0; transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }
        .bs-hero-fade-1 { animation: bs-hero-fade 1500ms ease-out 0ms both; }
        .bs-hero-fade-2 { animation: bs-hero-fade 1500ms ease-out 200ms both; }
        .bs-hero-fade-3 { animation: bs-hero-fade 1500ms ease-out 400ms both; }
        .bs-hero-fade-4 { animation: bs-hero-fade 1500ms ease-out 600ms both; }
        .bs-hero-photo  { animation: bs-photo-in 800ms ease-out 0ms both; }
        .bs-tile {
          transition: transform 600ms ease, filter 600ms ease, box-shadow 600ms ease;
          will-change: transform;
        }
        .bs-tile:hover {
          transform: rotate(0deg) translateY(-3px) !important;
          filter: sepia(0.05) brightness(1.08) saturate(1) !important;
          box-shadow: 0 14px 30px -18px rgba(0,0,0,0.25);
        }
        @media (prefers-reduced-motion: reduce) {
          .bs-hero-fade-1,
          .bs-hero-fade-2,
          .bs-hero-fade-3,
          .bs-hero-fade-4,
          .bs-hero-photo { animation: none !important; }
          .bs-tile,
          .bs-tile:hover { transform: none !important; transition: none !important; }
        }

        /* ─── MOBILE REFINEMENTS — gentle Uzbek beshik on phone ──────────── */
        @media (max-width: 768px) {
          /* Hero section padding tightens on phone */
          .beshik-variant .bs-hero {
            padding: 56px 20px 64px !important;
            min-height: auto !important;
          }
          /* Faint giant beshik silhouette behind title — more present on
             smaller screen but still subtle (4% opacity, full width). */
          .beshik-variant .bs-hero-bgshadow {
            opacity: 0.04 !important;
            top: 8% !important;
            width: 100% !important;
          }
          .beshik-variant .bs-hero-bgshadow svg {
            width: 100% !important;
            height: auto !important;
          }
          /* Birth-announcement stamp at top — only shown on mobile */
          .beshik-variant .bs-mobile-stamp { display: block !important; }

          /* "welcome little one" handwritten tagline */
          .beshik-variant .bs-hero-script {
            font-size: clamp(36px, 9vw, 48px) !important;
            line-height: 1.05 !important;
            margin-bottom: 14px !important;
          }
          /* Names — Lora italic 56-72px, sage */
          .beshik-variant .bs-hero-names {
            font-size: clamp(56px, 16vw, 72px) !important;
            line-height: 1 !important;
            margin-bottom: 18px !important;
          }
          /* Alla lullaby — italic Lora 18-20px, generous padding */
          .beshik-variant .bs-hero-alla {
            font-size: clamp(18px, 4.6vw, 20px) !important;
            line-height: 1.7 !important;
            padding: 6px 14px !important;
            margin: 4px auto 26px !important;
            max-width: 92% !important;
          }
          /* Hide the desktop comma-only version, show the footprint version */
          .beshik-variant .bs-hero-alla-desktop { display: none !important; }
          .beshik-variant .bs-hero-alla-mobile { display: inline !important; }

          /* Arched portrait — dominant: 88vw with carved arch border visible */
          .beshik-variant .bs-hero-photo-wrap {
            max-width: 88vw !important;
            width: 88vw !important;
            aspect-ratio: 4/5 !important;
            margin: 0 auto 28px !important;
            border-radius: 44vw 44vw 12px 12px !important;
          }
          /* Date stamp block */
          .beshik-variant .bs-hero-datebox {
            padding: 16px 22px !important;
          }
          .beshik-variant .bs-hero-datebox-date {
            font-size: 22px !important;
          }

          /* Story section padding */
          .beshik-variant .bs-section-story {
            padding: 56px 20px !important;
          }

          /* Triptych: vertical column on mobile */
          .beshik-variant .bs-section-triptych {
            padding: 56px 20px !important;
          }
          .beshik-variant .bs-triptych-grid {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }
          .beshik-variant .bs-tile {
            aspect-ratio: 4/5 !important;
            border-radius: 50% 50% 8px 8px / 38% 38% 8px 8px !important;
          }
          /* Mobile tile rotations — ±2° applied per nth-child */
          .beshik-variant .bs-tile:nth-child(1) {
            transform: rotate(-2deg) !important;
          }
          .beshik-variant .bs-tile:nth-child(2) {
            transform: rotate(0deg) !important;
          }
          .beshik-variant .bs-tile:nth-child(3) {
            transform: rotate(2deg) !important;
          }
          /* Album-page warm overlay (oat → sage at 6%) for mobile feel */
          .beshik-variant .bs-tile-overlay-mobile {
            display: block !important;
          }
          /* Show handwritten captions only on mobile */
          .beshik-variant .bs-tile-caption { display: block !important; }

          /* Footprint dividers — smaller, 60% width centered, 3 prints */
          .beshik-variant .bs-footprint-divider svg {
            width: 60vw !important;
            max-width: 220px !important;
            height: auto !important;
          }
          .beshik-variant .bs-footprint-third { display: inline !important; }

          /* RSVP section padding */
          .beshik-variant .bs-section-rsvp {
            padding: 0 16px !important;
          }
          /* RSVP buttons — make all primary action buttons chunky 56px tall.
             The first button in a flex pair is the "Albatta kelaman" yes
             button; we add a baby-foot SVG mark before its label as a
             welcoming, gentle cue. */
          .beshik-variant .bs-section-rsvp button {
            min-height: 56px !important;
            padding-top: 16px !important;
            padding-bottom: 16px !important;
            font-size: 14px !important;
          }
          /* Inline-svg baby-foot icon prepended to the primary RSVP button. */
          .beshik-variant .bs-section-rsvp div > button:first-child:not(:only-child)::before {
            content: "";
            display: inline-block;
            width: 16px;
            height: 16px;
            margin-right: 10px;
            vertical-align: -3px;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'><g fill='%23ffffff'><ellipse cx='11' cy='14' rx='4' ry='5.5'/><circle cx='6.5' cy='5' r='1.1'/><circle cx='10' cy='3.2' r='1.3'/><circle cx='13.5' cy='3.4' r='1.1'/><circle cx='16.5' cy='5' r='0.9'/></g></svg>");
            background-repeat: no-repeat;
            background-size: contain;
          }
        }
        @media (min-width: 769px) {
          .beshik-variant .bs-mobile-stamp { display: none; }
          .beshik-variant .bs-hero-alla-mobile { display: none; }
          .beshik-variant .bs-tile-overlay-mobile { display: none; }
          .beshik-variant .bs-tile-caption { display: none; }
          .beshik-variant .bs-footprint-third { display: none; }
        }
        @media (max-width: 768px) {
          .beshik-variant .bs-hero {
            padding: 64px 18px 48px !important;
          }
          .beshik-variant .bs-hero-names {
            font-size: clamp(36px, 12vw, 72px) !important;
          }
          .beshik-variant button { min-height: 44px; }
        }
        .beshik-variant button:focus-visible { outline: 2px solid ${t.accent}; outline-offset: 2px; }
      `}</style>

      {/* ═══ HERO ═══ */}
      <section
        className="bs-hero"
        style={{
          minHeight: "100vh",
          padding: "80px 48px",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Faint beshik silhouette behind the title — barely there. */}
        <div
          className="bs-hero-bgshadow"
          style={{
            position: "absolute",
            top: "12%",
            left: "50%",
            transform: "translateX(-50%)",
            opacity: 0.06,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <BeshikSilhouette color={t.accent} size={520} showRockerLine={false} />
        </div>

        <div style={{ maxWidth: 600, position: "relative" }}>
          {/* Mobile-only birth announcement stamp at the very top. */}
          <div
            className="bs-mobile-stamp"
            style={{
              display: "none",
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.3em",
              color: t.accent,
              marginBottom: 16,
              textTransform: "uppercase",
            }}
          >
            {tt("variant.bs01.hero.welcome_stamp")}
          </div>
          <div
            className="bs-hero-fade-1 bs-hero-script"
            style={{
              fontFamily: "var(--font-hand)",
              fontSize: 42,
              color: t.accent,
              marginBottom: 18,
            }}
          >
            {tt("variant.bs01.hero.script")}
          </div>

          <h1
            className="bs-hero-fade-2 bs-hero-names"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(56px, 9vw, 110px)",
              fontStyle: "italic",
              fontWeight: 400,
              lineHeight: 0.95,
              marginBottom: 18,
              color: t.accent,
            }}
          >
            {t.names}
          </h1>

          {/* Traditional Uzbek alla (lullaby) line — italic, script-warm.
              Desktop: comma-separated. Mobile: footprint-marker separated. */}
          <p
            className="bs-hero-fade-2 bs-hero-alla"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(17px, 2.2vw, 22px)",
              lineHeight: 1.5,
              color: `${t.ink}aa`,
              marginBottom: 28,
              maxWidth: 460,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <span className="bs-hero-alla-desktop">
              {tt("variant.bs01.hero.alla")}
            </span>
            <span className="bs-hero-alla-mobile" style={{ display: "none" }}>
              {tt("variant.bs01.hero.alla")}
            </span>
          </p>

          {/* Signature rocker — visible above the photo on hero. */}
          <div className="bs-hero-fade-3" style={{ marginBottom: 24, color: t.accent }}>
            <BeshikRocker
              color={t.accent}
              reduceMotion={reduceMotion}
              onSettle={handleRockerSettle}
            />
          </div>

          {/* Arched portrait. Same arch shape as the card layout for echo. */}
          <div
            className="bs-hero-photo bs-hero-photo-wrap"
            style={{
              maxWidth: 360,
              aspectRatio: "4/5",
              margin: "0 auto 32px",
              overflow: "hidden",
              borderRadius: "180px 180px 12px 12px",
              border: `1px solid ${t.accent}55`,
              boxShadow: `0 20px 40px -28px ${t.ink}88`,
            }}
          >
            <GuestPhoto
              src={p[0]}
              style={{ width: "100%", height: "100%" }}
              filter={PHOTO_FILTER}
              fallbackBg={t.bg}
              fallbackTone={t.accent}
            />
          </div>

          <p
            className="bs-hero-fade-4"
            style={{
              fontFamily: "var(--font-body)",
              fontStyle: "italic",
              fontSize: 19,
              lineHeight: 1.7,
              color: `${t.ink}cc`,
              marginBottom: 28,
            }}
          >
            {tt("variant.bs01.hero.body")}
          </p>

          <div
            className="bs-hero-fade-4 bs-hero-datebox"
            style={{
              display: "inline-block",
              padding: "20px 32px",
              border: `1px solid ${t.accent}`,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.3em",
                color: t.accent,
                marginBottom: 8,
              }}
            >
              {tt("variant.bs01.hero.datebox_eyebrow")}
            </div>
            <div className="bs-hero-datebox-date" style={{ fontFamily: t.font, fontSize: 28, fontStyle: "italic" }}>
              {t.date} · {t.venue}
            </div>
          </div>
        </div>
      </section>

      {/* footprint divider */}
      <div
        className="bs-footprint-divider"
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "20px 0",
          color: t.accent,
        }}
      >
        <FootprintDivider color={t.accent} />
      </div>

      {/* ═══ STORY ═══ */}
      <Reveal reduceMotion={reduceMotion} onReveal={() => play("swoosh")}>
        <div className="bs-section-story">
          <StorySection
            t={t}
            eyebrow={tt("variant.bs01.story.eyebrow")}
            title={tt("variant.bs01.story.title")}
            body={tt("variant.bs01.story.body")}
          />
        </div>
      </Reveal>

      {/* footprint divider */}
      {(p[1] || p[2] || p[3]) && (
        <div
          className="bs-footprint-divider"
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0 0",
            color: t.accent,
          }}
        >
          <FootprintDivider color={t.accent} />
        </div>
      )}

      {/* ═══ TRIPTYCH ═══ */}
      {(p[1] || p[2] || p[3]) && (
        <Reveal reduceMotion={reduceMotion} onReveal={() => play("swoosh")}>
          <section
            className="bs-section-triptych"
            style={{
              padding: "100px 48px",
              borderTop: `1px solid ${t.ink}22`,
              background: `${t.accent}08`,
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div
                style={{
                  fontFamily: "var(--font-hand)",
                  fontSize: 32,
                  color: t.accent,
                  marginBottom: 8,
                }}
              >
                {tt("variant.bs01.triptych.eyebrow")}
              </div>
              <h2
                style={{
                  fontFamily: t.font,
                  fontSize: "clamp(32px, 4vw, 44px)",
                  fontStyle: "italic",
                  fontWeight: 400,
                }}
              >
                {tt("variant.bs01.triptych.title")}
              </h2>
            </div>
            <div
              className="bs-triptych-grid"
              style={{
                maxWidth: 1080,
                margin: "0 auto",
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 24,
              }}
            >
              {[p[1], p[2], p[3] || p[0]].filter(Boolean).map((src, i) => {
                const captions = [
                  tt("variant.bs01.triptych.caption.0"),
                  tt("variant.bs01.triptych.caption.1"),
                  tt("variant.bs01.triptych.caption.2"),
                ];
                return (
                  <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div
                      className="bs-tile"
                      style={{
                        position: "relative",
                        aspectRatio: "4/5",
                        width: "100%",
                        borderRadius: "100px 100px 8px 8px",
                        overflow: "hidden",
                        border: `1px solid ${t.accent}33`,
                        transform: `rotate(${TRIPTYCH_ROTATIONS[i] ?? "0deg"})`,
                        background: t.bg,
                      }}
                    >
                      <GuestPhoto
                        src={src}
                        style={{ width: "100%", height: "100%" }}
                        filter={PHOTO_FILTER}
                        fallbackBg={t.bg}
                        fallbackTone={t.accent}
                      />
                      {/* soft oat gradient overlay — feels like an album page */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: `linear-gradient(180deg, ${t.bg}00 55%, ${t.bg}55 100%)`,
                          pointerEvents: "none",
                        }}
                      />
                      {/* mobile-only warm-tone album overlay (oat → sage 6%) */}
                      <div
                        className="bs-tile-overlay-mobile"
                        style={{
                          display: "none",
                          position: "absolute",
                          inset: 0,
                          background: `linear-gradient(160deg, ${t.bg}0F 0%, ${t.accent}0F 100%)`,
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                    {/* mobile-only handwritten caption */}
                    <div
                      className="bs-tile-caption"
                      style={{
                        display: "none",
                        fontFamily: "var(--font-hand)",
                        fontSize: 22,
                        color: t.accent,
                        marginTop: 10,
                        opacity: 0.85,
                      }}
                    >
                      {captions[i] ?? ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </Reveal>
      )}

      {/* footprint divider before RSVP */}
      <div
        className="bs-footprint-divider"
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "20px 0 0",
          color: t.accent,
        }}
      >
        <FootprintDivider color={t.accent} />
      </div>

      {/* ═══ RSVP ═══ */}
      <Reveal reduceMotion={reduceMotion} onReveal={() => play("swoosh")}>
        <div className="bs-section-rsvp">
          <RsvpSection t={t} isDark={isDark} {...rsvpProxy} label={tt("variant.bs01.rsvp.label")} />
        </div>
      </Reveal>

      <ShareSection t={t} />
      <GuestFooter t={t} />
    </div>
  );
}
